import React, { useState, useEffect } from 'react';
import { 
  Typography, Container, Paper, Button, Box, Dialog, DialogTitle, 
  DialogContent, DialogActions, TextField, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip, Tabs, Tab, IconButton, Alert
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useSearchParams } from 'react-router-dom';
import { Add as AddIcon, Check as CheckIcon, Close as CloseIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { getAllLeaves, getUserLeaves, applyLeave, updateLeaveStatus, deleteLeave } from '../../api/leaveAPI';
import { getAllAttendance } from '../../api/attendanceAPI';
import { getAllHolidays } from '../../api/holidayAPI';
import DeleteIcon from '@mui/icons-material/Delete';
import { getAllUsers } from '../../api/userAPI';
import { getMyRewards } from '../../api/employeeOfMonthAPI';
import moment from 'moment';
import useSnackbar from '../../hooks/useSnackbar';

const LEAVE_QUOTAS = {
  Sick: 7,
  Casual: 10,
  Planned: 8,
  Paid: 8
};

const leaveTypeLabel = (type) => {
  if (type === 'Earned' || type === 'Paid') return 'Planned';
  if (type === 'Reward') return 'Reward (Employee of the Month)';
  return type;
};

const getLeaveSortTime = (item) => {
  if (item?.createdAt) {
    const t = new Date(item.createdAt).getTime();
    if (!isNaN(t)) return t;
  }
  if (item?._id && typeof item._id === 'string' && item._id.length === 24) {
    const t = parseInt(item._id.substring(0, 8), 16) * 1000;
    if (!isNaN(t)) return t;
  }
  if (item?.dates && item.dates.length > 0) {
    const validTimes = item.dates.map(d => new Date(d).getTime()).filter(t => !isNaN(t));
    if (validTimes.length > 0) return Math.max(...validTimes);
  }
  if (item?.startDate) {
    const t = new Date(item.startDate).getTime();
    if (!isNaN(t)) return t;
  }
  return 0;
};

const sortLeavesDesc = (leaves) => {
  if (!Array.isArray(leaves)) return [];
  return [...leaves].sort((a, b) => getLeaveSortTime(b) - getLeaveSortTime(a));
};

const Leaves = () => {
  const { user } = useSelector((state) => state.tokens);
  const isAdmin = user?.permission === 'Admin' || user?.permission === 'HR Management';
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  
  const [activeTab, setActiveTab] = useState(0);
  const [myLeaves, setMyLeaves] = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [allAttendance, setAllAttendance] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Apply Leave Form State
  const [openApply, setOpenApply] = useState(false);
  const [formData, setFormData] = useState({ type: 'Casual', reason: '' });
  const [selectedDates, setSelectedDates] = useState([]);
  const [dateInput, setDateInput] = useState('');

  // Direct Leave Form State (Admin Only)
  const [openDirect, setOpenDirect] = useState(false);
  const [directFormData, setDirectFormData] = useState({ targetUser: '', type: 'Casual', reason: '' });
  const [directSelectedDates, setDirectSelectedDates] = useState([]);
  const [directDateInput, setDirectDateInput] = useState('');

  // Reject Leave State
  const [openReject, setOpenReject] = useState(false);
  const [rejectLeaveId, setRejectLeaveId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Delete Confirmation State
  const [openDelete, setOpenDelete] = useState(false);
  const [deleteLeaveId, setDeleteLeaveId] = useState(null);

  // Employee of the Month reward leave (1 extra paid day, 30-day validity)
  const [myRewards, setMyRewards] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const availableReward = myRewards.find(r => r.rewardLeave?.status === 'available');

  const fetchMyRewards = async () => {
    try {
      const res = await getMyRewards();
      setMyRewards(res.data?.data || []);
    } catch(err) { console.error(err); }
  };

  const fetchMyLeaves = async () => {
    try {
      if(!user || !user._id) return;
      const res = await getUserLeaves(user._id);
      if(res.data && res.data.success) {
        setMyLeaves(sortLeavesDesc(res.data.leaves));
      }
    } catch(err) { console.error(err); }
  };

  const fetchAllLeaves = async () => {
    try {
      const res = await getAllLeaves();
      if(res.data && res.data.success) {
        setAllLeaves(sortLeavesDesc(res.data.leaves));
      }
      
      const attRes = await getAllAttendance({ range: 'yearly' });
      if(attRes.data && attRes.data.success) {
        setAllAttendance(attRes.data.log);
      }

      const holRes = await getAllHolidays();
      if(holRes.data && holRes.data.success) {
        setHolidays(holRes.data.holidays);
      }
    } catch(err) { console.error(err); }
  };

  const fetchUsers = async () => {
    try {
      const res = await getAllUsers();
      if(res.data) {
        setUsers(res.data);
      }
    } catch(err) { console.error(err); }
  };

  useEffect(() => {
    fetchMyLeaves();
    fetchMyRewards();
    fetchAllLeaves();
    if(isAdmin) {
        fetchUsers();
    }
  }, [user, isAdmin]);

  const openRewardApply = () => {
    setFormData({ type: 'Reward', reason: 'Employee of the Month reward leave' });
    setSelectedDates([]);
    setDateInput('');
    setOpenApply(true);
  };

  // Deep link from the profile page: /leaves?apply=reward
  useEffect(() => {
    if (searchParams.get('apply') === 'reward' && availableReward) {
      openRewardApply();
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, availableReward]);

  const handleApplyClick = () => {
    setFormData({ type: 'Casual', reason: '' });
    setSelectedDates([]);
    setDateInput('');
    setOpenApply(true);
  };

  const handleApplyChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    // Reward leave is a single day, so drop extra dates when switching to it.
    if (e.target.name === 'type' && e.target.value === 'Reward') {
      setSelectedDates(selectedDates.slice(0, 1));
    }
  };

  const handleDirectClick = () => {
    setDirectFormData({ targetUser: '', type: 'Casual', reason: '' });
    setDirectSelectedDates([]);
    setDirectDateInput('');
    setOpenDirect(true);
  };

  const handleDirectChange = (e) => {
    setDirectFormData({ ...directFormData, [e.target.name]: e.target.value });
  };

  const submitApply = async () => {
    let datesToSubmit = [...selectedDates];
    if (datesToSubmit.length === 0 && dateInput) {
      datesToSubmit = [dateInput];
    }
    if (datesToSubmit.length === 0) {
      showSnackbar("Please add at least one leave date.", "error");
      return;
    }
    if (!formData.reason.trim()) {
      showSnackbar("Please provide a reason for the leave.", "error");
      return;
    }
    try {
      const res = await applyLeave({
        user: user._id,
        dates: datesToSubmit,
        type: formData.type,
        reason: formData.reason
      });
      if (res.data && res.data.success) {
        showSnackbar("Leave request submitted successfully!", "success");
        setOpenApply(false);
        setFormData({ type: 'Casual', reason: '' });
        setSelectedDates([]);
        setDateInput('');
        fetchMyLeaves();
        fetchMyRewards();
        if(isAdmin) fetchAllLeaves();
      } else {
        showSnackbar(res.data?.message || "Failed to submit leave request", "error");
      }
    } catch(err) {
      console.error(err);
      showSnackbar(err.response?.data?.message || err.message || "Failed to submit leave request", "error");
    }
  };

  const submitDirectLeave = async () => {
    let datesToSubmit = [...directSelectedDates];
    if (datesToSubmit.length === 0 && directDateInput) {
      datesToSubmit = [directDateInput];
    }
    if (!directFormData.targetUser) {
      showSnackbar("Please select an employee.", "error");
      return;
    }
    if (datesToSubmit.length === 0) {
      showSnackbar("Please add at least one leave date.", "error");
      return;
    }
    if (!directFormData.reason.trim()) {
      showSnackbar("Please provide a reason for the leave.", "error");
      return;
    }
    try {
      const res = await applyLeave({
        user: directFormData.targetUser,
        dates: datesToSubmit,
        type: directFormData.type,
        reason: directFormData.reason,
        status: 'Approved',
        approvedBy: user._id
      });
      if (res.data && res.data.success) {
        showSnackbar("Direct leave assigned successfully!", "success");
        setOpenDirect(false);
        setDirectFormData({ targetUser: '', type: 'Casual', reason: '' });
        setDirectSelectedDates([]);
        setDirectDateInput('');
        fetchMyLeaves();
        if(isAdmin) fetchAllLeaves();
      } else {
        showSnackbar(res.data?.message || "Failed to assign direct leave", "error");
      }
    } catch(err) {
      console.error(err);
      showSnackbar(err.response?.data?.message || err.message || "Failed to assign direct leave", "error");
    }
  };

  const currentDate = moment();
  let startYear = currentDate.year();
  let endYear = currentDate.year() + 1;
  if (currentDate.month() < 3) { // 0=Jan, 1=Feb, 2=Mar
    startYear = currentDate.year() - 1;
    endYear = currentDate.year();
  }
  const fyStart = moment(`${startYear}-04-01`);
  const fyEnd = moment(`${endYear}-03-31`).endOf('day');

  const leaveStats = React.useMemo(() => {
    const stats = {};
    
    // Initialize stats for Office Employees
    users.forEach(u => {
      if (u.isActive === false || !u.isOfficeEmp) return;
      
      const uid = u.id || u._id;
      stats[uid] = {
        name: u.fullName || 'Unknown',
        total: 0,
        casual: 0,
        sick: 0,
        planned: 0,
        paid: 0,
        unpaid: 0,
        halfDays: 0,
        unapprovedAbsences: 0
      };
    });

    allLeaves.forEach(leave => {
      if (leave.status !== 'Approved') return;
      
      const validDates = (leave.dates || [])
        .filter(d => moment(d).isBetween(fyStart, fyEnd, 'day', '[]'))
        .filter(d => !holidays.some(h => moment(h.date).isSame(moment(d), 'day')));

      if (validDates.length === 0) return;

      const userId = leave.user?._id;
      if(!userId || !stats[userId]) return;

      stats[userId].total += validDates.length;
      if(leave.type === 'Casual') stats[userId].casual += validDates.length;
      if(leave.type === 'Sick') stats[userId].sick += validDates.length;
      if(leave.type === 'Earned' || leave.type === 'Paid' || leave.type === 'Planned') {
        stats[userId].paid += validDates.length;
        stats[userId].planned += validDates.length;
      }
      if(leave.type === 'Unpaid') stats[userId].unpaid += validDates.length;
    });

    // Add Half-Days from Attendance (Deduct from Casual Leave)
    allAttendance.forEach(log => {
      if (log.status !== 'Half-day' || log.halfDayStatus !== 'Approved') return;
      if (!moment(log.date).isBetween(fyStart, fyEnd, 'day', '[]')) return;
      
      const userId = log.user?._id;
      if(!userId || !stats[userId]) return;
      
      stats[userId].halfDays = (stats[userId].halfDays || 0) + 1;
      stats[userId].casual += 0.5;
      stats[userId].total += 0.5;
    });

    // Calculate Unapproved Absences (from fyStart up to yesterday)
    const today = moment().startOf('day');
    const endCalculations = moment.min(fyEnd, today.clone().subtract(1, 'day'));
    
    let absenceStart = fyStart.clone();
    if (absenceStart.year() === 2026) {
      absenceStart = moment('2026-06-01');
    }
    const dateIterator = absenceStart.clone();
    const workingDays = [];
    while (dateIterator.isSameOrBefore(endCalculations, 'day')) {
      const isSunday = dateIterator.day() === 0;
      const dateStr = dateIterator.format('YYYY-MM-DD');
      const isHoliday = holidays.some(h => moment(h.date).format('YYYY-MM-DD') === dateStr);
      if (!isSunday && !isHoliday) {
        workingDays.push(dateStr);
      }
      dateIterator.add(1, 'day');
    }

    users.forEach(u => {
      const uid = u.id || u._id;
      if (!stats[uid]) return;
      
      if (u.isActive === false || !u.isOfficeEmp) return;

      const uidString = uid.toString();
      let joinDate = moment('2000-01-01');
      if (uidString.length === 24) {
        joinDate = moment(new Date(parseInt(uidString.substring(0, 8), 16) * 1000)).startOf('day');
      }

      workingDays.forEach(wd => {
        // Skip checking absences for dates before the user joined
        if (moment(wd).isBefore(joinDate, 'day')) {
          return;
        }

        // Did they attend?
        const attended = allAttendance.some(log => 
          log.user?._id === uid && 
          moment(log.date).format('YYYY-MM-DD') === wd && 
          log.status !== 'Absent'
        );
        if (attended) return;

        // Do they have approved leave?
        const hasLeave = allLeaves.some(l => 
          l.user?._id === uid && 
          l.status === 'Approved' && 
          (l.dates || []).some(d => moment(d).format('YYYY-MM-DD') === wd)
        );
        if (hasLeave) return;

        // Absent without leave!
        stats[uid].unapprovedAbsences += 1;
        stats[uid].casual += 1;
        stats[uid].total += 1;
      });
    });

    // Calculate Available
    Object.keys(stats).forEach(id => {
      stats[id].availableSick = Math.max(0, LEAVE_QUOTAS.Sick - stats[id].sick);
      stats[id].availableCasual = Math.max(0, LEAVE_QUOTAS.Casual - stats[id].casual);
      stats[id].availablePlanned = Math.max(0, (LEAVE_QUOTAS.Planned || LEAVE_QUOTAS.Paid) - (stats[id].planned || stats[id].paid));
      stats[id].availablePaid = stats[id].availablePlanned;
    });

    return stats;
  }, [allLeaves, allAttendance, holidays, users, fyStart, fyEnd]);

  // For Admin display
  const allUserStats = React.useMemo(() => Object.values(leaveStats), [leaveStats]);
  
  // For User summary
  const mySummary = leaveStats[user?._id] || { 
    sick: 0, casual: 0, planned: 0, paid: 0, 
    availableSick: LEAVE_QUOTAS.Sick, 
    availableCasual: LEAVE_QUOTAS.Casual, 
    availablePlanned: LEAVE_QUOTAS.Planned || LEAVE_QUOTAS.Paid,
    availablePaid: LEAVE_QUOTAS.Planned || LEAVE_QUOTAS.Paid 
  };

  const handleApprove = async (id) => {
    try {
      const res = await updateLeaveStatus(id, { status: "Approved", approvedBy: user._id });
      if (res.data && res.data.success) {
        showSnackbar("Leave request approved!", "success");
      }
      fetchAllLeaves();
      fetchMyLeaves();
    } catch(err) {
      console.error(err);
      showSnackbar(err.response?.data?.message || "Failed to approve leave request", "error");
    }
  };

  const openRejectDialog = (id) => {
    setRejectLeaveId(id);
    setRejectionReason('');
    setOpenReject(true);
  };

  const handleReject = async () => {
    try {
      const res = await updateLeaveStatus(rejectLeaveId, { 
        status: "Rejected", 
        approvedBy: user._id,
        rejectionReason 
      });
      if (res.data && res.data.success) {
        showSnackbar("Leave request rejected.", "info");
      }
      setOpenReject(false);
      fetchAllLeaves();
      fetchMyLeaves();
    } catch(err) {
      console.error(err);
      showSnackbar(err.response?.data?.message || "Failed to reject leave request", "error");
    }
  };

  const handleRemove = async (id) => {
    if(!window.confirm("Are you sure you want to cancel this leave request?")) return;
    try {
      const res = await updateLeaveStatus(id, { status: "Cancelled" });
      if (res.data && res.data.success) {
        showSnackbar("Leave request cancelled successfully.", "info");
      }
      fetchMyLeaves();
      fetchMyRewards();
      if(isAdmin) fetchAllLeaves();
    } catch(err) {
      console.error(err);
      showSnackbar(err.response?.data?.message || "Failed to cancel leave request", "error");
    }
  };

  const openDeleteDialog = (id) => {
    setDeleteLeaveId(id);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    try {
      const res = await deleteLeave(deleteLeaveId);
      if (res.data && res.data.success) {
        showSnackbar("Leave request deleted successfully.", "info");
      }
      setOpenDelete(false);
      fetchMyLeaves();
      if(isAdmin) fetchAllLeaves();
    } catch(err) {
      console.error(err);
      showSnackbar(err.response?.data?.message || "Failed to delete leave request", "error");
    }
  };

  const getStatusChip = (status) => {
    if (status === 'Approved') return <Chip label="Approved" color="success" size="small" />;
    if (status === 'Rejected') return <Chip label="Rejected" color="error" size="small" />;
    if (status === 'Cancelled') return <Chip label="Cancelled" sx={{ bgcolor: '#e0e0e0', color: '#757575' }} size="small" />;
    return <Chip label="Pending" color="warning" size="small" />;
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">Leave Management</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isAdmin && (
            <Button variant="contained" color="secondary" onClick={handleDirectClick}>
              Assign Direct Leave
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={handleApplyClick}>
            Apply Leave
          </Button>
        </Box>
      </Box>

      {isAdmin && (
        <Tabs value={activeTab} onChange={(e, val) => setActiveTab(val)} sx={{ mb: 2 }}>
          <Tab label="My Leaves" />
          <Tab label="Team Requests" />
          <Tab label="Leave Balances" />
        </Tabs>
      )}

      {/* MY LEAVES TAB */}
      {(activeTab === 0 || !isAdmin) && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>My Leave Summary (Available / Total)</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 4 }}>
            {[
              { label: 'Sick Leave (SL)', used: mySummary.sick, quota: LEAVE_QUOTAS.Sick, color: '#ef5350' },
              { label: 'Casual Leave (CL)', used: mySummary.casual, quota: LEAVE_QUOTAS.Casual, color: '#ffa726' },
              { label: 'Planned Leave (PL)', used: mySummary.planned ?? mySummary.paid, quota: LEAVE_QUOTAS.Planned || LEAVE_QUOTAS.Paid, color: '#66bb6a' },
            ].map((card) => (
              <Paper key={card.label} sx={{ p: 2, textAlign: 'center', borderRadius: 2, border: `1px solid ${card.color}40` }}>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">{card.label}</Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: card.color, my: 1 }}>
                  {Math.max(0, card.quota - card.used)} / {card.quota}
                </Typography>
                <Typography variant="caption" color="text.secondary">Days Available</Typography>
              </Paper>
            ))}
            {availableReward && (
              <Paper
                onClick={openRewardApply}
                sx={{ p: 2, textAlign: 'center', borderRadius: 2, cursor: 'pointer', background: 'linear-gradient(135deg, #f7d774 0%, #e2a93b 100%)', color: '#4a3000' }}
              >
                <Typography variant="caption" fontWeight="bold" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <EmojiEventsIcon sx={{ fontSize: 16 }} /> Reward Leave (EOTM)
                </Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ my: 1 }}>1 / 1</Typography>
                <Typography variant="caption" fontWeight={600}>
                  Valid till {moment.utc(availableReward.rewardLeaveValidUntil).format('DD MMM YYYY')}
                </Typography>
              </Paper>
            )}
          </Box>

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell><b>Type</b></TableCell>
                <TableCell><b>Duration</b></TableCell>
                <TableCell><b>Reason</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Admin Feedback</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {myLeaves.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center">No leave requests found.</TableCell></TableRow>
              ) : sortLeavesDesc(myLeaves).map((leave) => (
                <TableRow key={leave._id}>
                  <TableCell>{leaveTypeLabel(leave.type)}</TableCell>
                  <TableCell>
                    {leave.dates && leave.dates.length > 0 
                      ? leave.dates.map(d => moment(d).format('MMM DD')).join(', ')
                      : `${moment(leave.startDate).format('MMM DD')} - ${moment(leave.endDate).format('MMM DD')}`}
                  </TableCell>
                  <TableCell>{leave.reason}</TableCell>
                  <TableCell>{getStatusChip(leave.status)}</TableCell>
                  <TableCell sx={{ color: leave.status === 'Rejected' ? 'error.main' : 'inherit' }}>
                    {leave.rejectionReason || '-'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {/* User can remove only BEFORE the leave starts and if Pending. Admin can remove it at any time (if not already cancelled). */}
                      {leave.status !== 'Cancelled' && (isAdmin || (leave.status === 'Pending' && leave.dates && leave.dates.length > 0 && moment(leave.dates[0]).isAfter(moment(), 'day'))) && (
                        <Button size="small" variant="outlined" color="error" onClick={() => handleRemove(leave._id)}>
                          Remove
                        </Button>
                      )}
                      {isAdmin && (
                        <IconButton size="small" color="error" onClick={() => openDeleteDialog(leave._id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </Box>
      )}

      {/* ADMIN REQUESTS TAB */}
      {isAdmin && activeTab === 1 && (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell><b>Employee Name</b></TableCell>
                <TableCell><b>Type</b></TableCell>
                <TableCell><b>Duration</b></TableCell>
                <TableCell><b>Reason</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Actions</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allLeaves.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center">No requests pending.</TableCell></TableRow>
              ) : sortLeavesDesc(allLeaves).map((req) => (
                <TableRow key={req._id}>
                  <TableCell>{req.user?.fullName || 'Unknown User'}</TableCell>
                  <TableCell>{leaveTypeLabel(req.type)}</TableCell>
                  <TableCell>
                    {req.dates && req.dates.length > 0 
                      ? req.dates.map(d => moment(d).format('MMM DD')).join(', ')
                      : `${moment(req.startDate).format('MMM DD')} - ${moment(req.endDate).format('MMM DD')}`}
                  </TableCell>
                  <TableCell>{req.reason}</TableCell>
                  <TableCell>{getStatusChip(req.status)}</TableCell>
                  <TableCell>
                    {req.status === 'Pending' && (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button 
                          variant="contained" 
                          color="success" 
                          size="small"
                          onClick={() => handleApprove(req._id)}
                        >
                          Approve
                        </Button>
                        <Button 
                          variant="contained" 
                          color="error" 
                          size="small"
                          onClick={() => openRejectDialog(req._id)}
                        >
                          Reject
                        </Button>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: req.status === 'Pending' ? 1 : 0, gap: 1 }}>
                      {req.status === 'Rejected' && (
                        <Typography variant="caption" color="error">
                          Reason: {req.rejectionReason}
                        </Typography>
                      )}
                      {isAdmin && (
                        <>
                          {req.status !== 'Cancelled' && (
                            <Button size="small" variant="outlined" color="error" onClick={() => handleRemove(req._id)} sx={{ textTransform: 'none' }}>
                               Cancel
                            </Button>
                          )}
                          <IconButton size="small" color="error" onClick={() => openDeleteDialog(req._id)} sx={{ ml: 'auto' }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ADMIN BALANCES TAB */}
      {isAdmin && activeTab === 2 && (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Box sx={{ p: 2, bgcolor: '#e2e8f0', borderBottom: '1px solid #cbd5e1' }}>
            <Typography variant="subtitle1" fontWeight="bold">
              Leave counts for current Financial Year ({fyStart.format('DD MMM YYYY')} - {fyEnd.format('DD MMM YYYY')})
            </Typography>
          </Box>
          <Table>
            <TableHead sx={{ bgcolor: '#f5f5f5' }}>
              <TableRow>
                <TableCell><b>Employee Name</b></TableCell>
                <TableCell><b>Casual (Used/Avail)</b></TableCell>
                <TableCell><b>Sick (Used/Avail)</b></TableCell>
                <TableCell><b>Planned (Used/Avail)</b></TableCell>
                <TableCell><b>Unpaid</b></TableCell>
                <TableCell><b>Half-Days</b></TableCell>
                <TableCell><b>Unapproved Absences</b></TableCell>
                <TableCell><b>Total Leaves</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allUserStats.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center">No active employees found.</TableCell></TableRow>
              ) : allUserStats.map((stat, idx) => (
                <TableRow key={idx}>
                  <TableCell>{stat.name}</TableCell>
                  <TableCell>{stat.casual} / {LEAVE_QUOTAS.Casual}</TableCell>
                  <TableCell>{stat.sick} / {LEAVE_QUOTAS.Sick}</TableCell>
                  <TableCell>{stat.planned ?? stat.paid} / {LEAVE_QUOTAS.Planned || LEAVE_QUOTAS.Paid}</TableCell>
                  <TableCell>{stat.unpaid}</TableCell>
                  <TableCell>{stat.halfDays || 0}</TableCell>
                  <TableCell sx={{ color: stat.unapprovedAbsences > 0 ? 'error.main' : 'inherit', fontWeight: stat.unapprovedAbsences > 0 ? 'bold' : 'normal' }}>
                    {stat.unapprovedAbsences || 0}
                  </TableCell>
                  <TableCell><b>{stat.total}</b></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* APPLY LEAVE DIALOG */}
      <Dialog open={openApply} onClose={() => setOpenApply(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Leave</DialogTitle>
        <DialogContent dividers>          <TextField
            select
            margin="dense"
            label="Leave Type"
            name="type"
            fullWidth
            variant="outlined"
            value={formData.type}
            onChange={handleApplyChange}
            sx={{ mb: 2 }}
          >
            <MenuItem value="Casual">Casual (CL)</MenuItem>
            <MenuItem value="Sick">Sick (SL)</MenuItem>
            <MenuItem value="Planned">Planned (PL)</MenuItem>
            <MenuItem value="Unpaid">Unpaid</MenuItem>
            {(availableReward || formData.type === 'Reward') && (
              <MenuItem value="Reward">🏆 Reward Leave (Employee of the Month)</MenuItem>
            )}
          </TextField>
          {formData.type === 'Reward' && availableReward && (
            <Alert severity="success" icon={<EmojiEventsIcon />} sx={{ mb: 2 }}>
              One extra paid day for being Employee of the Month. Pick a single date between{' '}
              <b>{moment.utc(availableReward.announcedAt).format('DD MMM YYYY')}</b> and{' '}
              <b>{moment.utc(availableReward.rewardLeaveValidUntil).format('DD MMM YYYY')}</b>.
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label="Pick Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                inputProps={formData.type === 'Reward' && availableReward ? {
                  min: moment.utc(availableReward.announcedAt).format('YYYY-MM-DD'),
                  max: moment.utc(availableReward.rewardLeaveValidUntil).format('YYYY-MM-DD'),
                } : undefined}
                value={dateInput}
                onChange={(e) => setDateInput(e.target.value)}
              />
              <Button
                variant="outlined"
                disabled={formData.type === 'Reward' && selectedDates.length >= 1}
                onClick={() => {
                  if (dateInput && !selectedDates.includes(dateInput)) {
                    setSelectedDates([...selectedDates, dateInput]);
                    setDateInput('');
                  }
                }}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {selectedDates.map(d => (
                <Chip 
                  key={d} 
                  label={moment(d).format('DD MMM YYYY')} 
                  onDelete={() => setSelectedDates(selectedDates.filter(sd => sd !== d))} 
                  color="primary" 
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
          <TextField
            label="Reason"
            name="reason"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={formData.reason}
            onChange={handleApplyChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenApply(false)}>Cancel</Button>
          <Button onClick={submitApply} variant="contained" disabled={(selectedDates.length === 0 && !dateInput) || !formData.reason?.trim()}>
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      {/* ASSIGN DIRECT LEAVE DIALOG */}
      <Dialog open={openDirect} onClose={() => setOpenDirect(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Direct Leave</DialogTitle>
        <DialogContent dividers>
          <TextField
            select
            margin="dense"
            label="Select Employee"
            name="targetUser"
            fullWidth
            variant="outlined"
            value={directFormData.targetUser}
            onChange={handleDirectChange}
            sx={{ mb: 2 }}
          >
            {users.map((u) => (
              <MenuItem key={u.id || u._id} value={u.id || u._id}>
                {u.fullName} ({u.email})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="Leave Type"
            name="type"
            fullWidth
            variant="outlined"
            value={directFormData.type}
            onChange={handleDirectChange}
            sx={{ mb: 2 }}
          >
            <MenuItem value="Casual">Casual (CL)</MenuItem>
            <MenuItem value="Sick">Sick (SL)</MenuItem>
            <MenuItem value="Planned">Planned (PL)</MenuItem>
            <MenuItem value="Unpaid">Unpaid</MenuItem>
            <MenuItem value="Reward">🏆 Reward Leave (Employee of the Month)</MenuItem>
          </TextField>
          {directFormData.type === 'Reward' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Only for an Employee of the Month with an unused reward leave — exactly one date within its 30-day validity window.
            </Alert>
          )}
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                label="Pick Date"
                type="date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={directDateInput}
                onChange={(e) => setDirectDateInput(e.target.value)}
              />
              <Button 
                variant="outlined" 
                onClick={() => {
                  if (directDateInput && !directSelectedDates.includes(directDateInput)) {
                    setDirectSelectedDates([...directSelectedDates, directDateInput]);
                    setDirectDateInput('');
                  }
                }}
              >
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {directSelectedDates.map(d => (
                <Chip 
                  key={d} 
                  label={moment(d).format('DD MMM YYYY')} 
                  onDelete={() => setDirectSelectedDates(directSelectedDates.filter(sd => sd !== d))} 
                  color="primary" 
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
          <TextField
            label="Reason"
            name="reason"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={directFormData.reason}
            onChange={handleDirectChange}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDirect(false)}>Cancel</Button>
          <Button onClick={submitDirectLeave} color="secondary" variant="contained" disabled={(directSelectedDates.length === 0 && !directDateInput) || !directFormData.reason?.trim() || !directFormData.targetUser}>
            Assign Direct Leave
          </Button>
        </DialogActions>
      </Dialog>

      {/* REJECT LEAVE REASON DIALOG */}
      <Dialog open={openReject} onClose={() => setOpenReject(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Provide Rejection Reason</DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            label="Why are you rejecting this leave?"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReject(false)}>Cancel</Button>
          <Button onClick={handleReject} color="error" variant="contained" disabled={!rejectionReason}>
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>

      {/* DELETE CONFIRMATION DIALOG */}
      <Dialog open={openDelete} onClose={() => setOpenDelete(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to <b>permanently delete</b> this leave request from the database? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDelete(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      <SnackbarComponent />
    </Container>
  );
};

export default Leaves;
